import React from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2, ChevronDown, ChevronUp, Sparkles } from 'lucide-react';
import type { StatsBoxComponent, StatsBoxItem } from '../../../types/builder';
import { STATS_BOX_DEFAULT_ICONS } from '../../../utils/statsBoxIcons';
import { useBuilderStore } from '../../../store/builderStore';
import { ColorPicker } from '../../Common/ColorPicker';
import { ImageUploader } from '../../Common/ImageUploader';
import { ToggleSwitch } from '../../Common/ToggleSwitch';

interface Props {
  component: StatsBoxComponent;
}

const inputCls =
  'w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500';

export const StatsBoxSettings: React.FC<Props> = ({ component }) => {
  const { t } = useTranslation('builder');
  const { updateComponent } = useBuilderStore();
  const [openIdx, setOpenIdx] = React.useState<number>(0);

  const handleUpdate = (updates: Partial<StatsBoxComponent['settings']>) => {
    updateComponent(component.id, {
      settings: { ...component.settings, ...updates },
    });
  };

  const updateItem = (idx: number, patch: Partial<StatsBoxItem>) => {
    const next = component.settings.items.map((it, i) =>
      i === idx ? { ...it, ...patch } : it,
    );
    handleUpdate({ items: next });
  };

  const addItem = () => {
    const next: StatsBoxItem = {
      id: `sb-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      icon: STATS_BOX_DEFAULT_ICONS.online,
      label: t('settings.statsBox.newItemLabel'),
      min: 1000,
      max: 9999,
      delta: 50,
      highlight: false,
    };
    handleUpdate({ items: [...component.settings.items, next] });
    setOpenIdx(component.settings.items.length);
  };

  const removeItem = (idx: number) => {
    if (component.settings.items.length <= 1) return;
    handleUpdate({
      items: component.settings.items.filter((_, i) => i !== idx),
    });
  };

  return (
    <div className="space-y-6">
      {/* Items */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium">
            {t('settings.statsBox.heading', { count: component.settings.items.length })}
          </label>
          <button
            type="button"
            onClick={addItem}
            className="flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-blue-600 text-white hover:bg-blue-700"
          >
            <Plus size={14} /> {t('settings.statsBox.addBox')}
          </button>
        </div>

        <div className="space-y-2">
          {component.settings.items.map((item, idx) => {
            const open = openIdx === idx;
            return (
              <div key={item.id} className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                <button
                  type="button"
                  onClick={() => setOpenIdx(open ? -1 : idx)}
                  className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-gray-50"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <img
                      src={item.icon}
                      alt=""
                      className="w-7 h-7 object-contain flex-shrink-0"
                    />
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">
                        {item.label || t('settings.statsBox.boxFallback', { n: idx + 1 })}
                      </div>
                      <div className="text-[11px] text-gray-500 truncate">
                        {item.min.toLocaleString()} – {item.max.toLocaleString()}
                        {item.highlight ? ` ${t('settings.statsBox.highlightSuffix')}` : ''}
                      </div>
                    </div>
                  </div>
                  {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </button>

                {open && (
                  <div className="p-3 space-y-3 border-t border-gray-100">
                    <ImageUploader
                      label={t('settings.statsBox.iconImage')}
                      value={item.icon}
                      onChange={(url) => updateItem(idx, { icon: url })}
                      aspectRatio="icon"
                    />

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">{t('settings.statsBox.label')}</label>
                      <input
                        type="text"
                        value={item.label}
                        onChange={(e) => updateItem(idx, { label: e.target.value })}
                        className={inputCls}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">{t('settings.statsBox.min')}</label>
                        <input
                          type="number"
                          value={item.min}
                          onChange={(e) => updateItem(idx, { min: parseInt(e.target.value) || 0 })}
                          className={inputCls}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">{t('settings.statsBox.max')}</label>
                        <input
                          type="number"
                          value={item.max}
                          onChange={(e) => updateItem(idx, { max: parseInt(e.target.value) || 0 })}
                          className={inputCls}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        {t('settings.statsBox.deltaPerTick')}
                      </label>
                      <input
                        type="number"
                        value={item.delta}
                        onChange={(e) =>
                          updateItem(idx, { delta: Math.max(0, parseInt(e.target.value) || 0) })
                        }
                        className={inputCls}
                      />
                    </div>

                    <ToggleSwitch
                      label={t('settings.statsBox.highlightToggle')}
                      checked={!!item.highlight}
                      onChange={(checked) => updateItem(idx, { highlight: checked })}
                    />

                    <button
                      type="button"
                      onClick={() => removeItem(idx)}
                      disabled={component.settings.items.length <= 1}
                      className="flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-red-50 text-red-600 hover:bg-red-100 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <Trash2 size={12} /> {t('settings.statsBox.remove')}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Update interval */}
      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.statsBox.updateInterval')}</label>
        <input
          type="range"
          min={300}
          max={5000}
          step={100}
          value={component.settings.updateInterval}
          onChange={(e) => handleUpdate({ updateInterval: parseInt(e.target.value) })}
          className="w-full"
        />
        <div className="text-xs text-gray-500 mt-1">{component.settings.updateInterval}ms</div>
      </div>

      {/* Colors */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium mb-2">{t('settings.statsBox.boxTop')}</label>
          <ColorPicker
            color={component.settings.boxColorTop}
            onChange={(c) => handleUpdate({ boxColorTop: c })}
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-2">{t('settings.statsBox.boxBottom')}</label>
          <ColorPicker
            color={component.settings.boxColorBottom}
            onChange={(c) => handleUpdate({ boxColorBottom: c })}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium mb-2 flex items-center gap-1">
            <Sparkles size={14} /> {t('settings.statsBox.accentGlow')}
          </label>
          <ColorPicker
            color={component.settings.accentColor}
            onChange={(c) => handleUpdate({ accentColor: c })}
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-2">{t('settings.common.textColor')}</label>
          <ColorPicker
            color={component.settings.textColor}
            onChange={(c) => handleUpdate({ textColor: c })}
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.statsBox.backgroundAround')}</label>
        <ColorPicker
          color={component.settings.backgroundColor}
          onChange={(c) => handleUpdate({ backgroundColor: c })}
        />
      </div>

      {/* Sizing */}
      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.common.iconSize')}</label>
        <input
          type="range"
          min={32}
          max={96}
          value={component.settings.iconSize}
          onChange={(e) => handleUpdate({ iconSize: parseInt(e.target.value) })}
          className="w-full"
        />
        <div className="text-xs text-gray-500 mt-1">{component.settings.iconSize}px</div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium mb-2">{t('settings.statsBox.valueFont')}</label>
          <input
            type="number"
            value={component.settings.valueFontSize}
            onChange={(e) =>
              handleUpdate({ valueFontSize: Math.max(10, parseInt(e.target.value) || 0) })
            }
            className={inputCls}
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-2">{t('settings.statsBox.labelFont')}</label>
          <input
            type="number"
            value={component.settings.labelFontSize}
            onChange={(e) =>
              handleUpdate({ labelFontSize: Math.max(8, parseInt(e.target.value) || 0) })
            }
            className={inputCls}
          />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="block text-sm font-medium mb-2">{t('settings.statsBox.radius')}</label>
          <input
            type="number"
            value={component.settings.borderRadius}
            onChange={(e) =>
              handleUpdate({ borderRadius: Math.max(0, parseInt(e.target.value) || 0) })
            }
            className={inputCls}
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-2">{t('settings.statsBox.gap')}</label>
          <input
            type="number"
            value={component.settings.gap}
            onChange={(e) => handleUpdate({ gap: Math.max(0, parseInt(e.target.value) || 0) })}
            className={inputCls}
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-2">{t('settings.common.padding')}</label>
          <input
            type="number"
            value={component.settings.padding}
            onChange={(e) =>
              handleUpdate({ padding: Math.max(0, parseInt(e.target.value) || 0) })
            }
            className={inputCls}
          />
        </div>
      </div>
    </div>
  );
};
